import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Tooltip,
    Stack,
    LinearProgress,
    Chip,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Checkbox,
    FormControlLabel,
    useTheme,
    alpha,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PersonAdd as PersonAddIcon,
    RemoveCircleOutline as RemovePocIcon,
    Business as BusinessIcon,
    Contacts as ContactsIcon,
    Search as SearchIcon,
    SwapHoriz as ConvertIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import { PROJECT_TYPE_PROPOSAL } from '../utils/projectTypes';
import projectsService from '../services/projectsService';
import { extractClientFromText } from '../services/aiChatService';
import { usePlainTextClientDrop } from '../hooks/usePlainTextClientDrop';
import { useFormFieldErrors, REQUIRED_FIELDS_MESSAGE } from '../hooks/useFormFieldErrors';
import FormValidationAlert from '../components/FormValidationAlert';
import { Client, ClientKind } from '../types';
import LoadingAnimation from '../components/LoadingAnimation';
import AccessDenied from '../components/AccessDenied';
import ContactKindChip, { ContactBlacklistChip } from '../components/ContactKindChip';

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };

type KindFilter = 'all' | ClientKind;
type PocFormRow = { full_name: string; email: string; phone: string; job_position: string };

const contactKind = (client: Client): ClientKind => client.kind ?? 'client';

const emptyPocRow = (): PocFormRow => ({ full_name: '', email: '', phone: '', job_position: '' });

const clientToPocRows = (client: Client): PocFormRow[] => {
    const list = client.pocs?.length
        ? client.pocs.map((p) => ({
              full_name: p.full_name || '',
              email: p.email || '',
              phone: p.phone || '',
              job_position: p.job_position || '',
          }))
        : [emptyPocRow()];
    return list;
};

const normalizeClientKey = (value: string): string =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');

const clientSearchHaystack = (client: Client): string => {
    const pocParts = (client.pocs ?? []).flatMap((p) => [p.full_name, p.email, p.phone, p.job_position]);
    return [client.name, client.abbreviation, client.main_contact, ...pocParts]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
};

const mergePocRows = (baseRows: PocFormRow[], incomingRows: PocFormRow[]): PocFormRow[] => {
    const out = baseRows
        .map((r) => ({
            full_name: r.full_name.trim(),
            email: r.email.trim(),
            phone: r.phone.trim(),
            job_position: r.job_position.trim(),
        }))
        .filter((r) => r.full_name);

    const existingKeys = new Set(out.map((r) => normalizeClientKey(r.full_name)));
    for (const row of incomingRows) {
        const clean = {
            full_name: row.full_name.trim(),
            email: row.email.trim(),
            phone: row.phone.trim(),
            job_position: row.job_position.trim(),
        };
        if (!clean.full_name) continue;
        const key = normalizeClientKey(clean.full_name);
        if (existingKeys.has(key)) {
            const idx = out.findIndex((r) => normalizeClientKey(r.full_name) === key);
            if (idx >= 0 && !out[idx].email && clean.email) {
                out[idx] = { ...out[idx], email: clean.email };
            }
            continue;
        }
        out.push(clean);
        existingKeys.add(key);
    }
    return out.length > 0 ? out : [emptyPocRow()];
};

const mapExtractedPocs = (
    pocs: Array<{ full_name: string; email?: string; phone: string; job_position: string }> | undefined,
): PocFormRow[] =>
    pocs?.length
        ? pocs.map((p) => ({
              full_name: p.full_name || '',
              email: p.email || '',
              phone: p.phone || '',
              job_position: p.job_position || '',
          }))
        : [];

type ClientRowDropTargetProps = {
    enabled: boolean;
    onTextDropped: (text: string) => Promise<void>;
    onFileOnlyDrop: () => void;
    onDoubleClick?: () => void;
    rowTitle?: string;
    rowAriaLabel?: string;
    sx?: object;
    children: React.ReactNode;
};

const ClientRowDropTarget: React.FC<ClientRowDropTargetProps> = ({
    enabled,
    onTextDropped,
    onFileOnlyDrop,
    onDoubleClick,
    rowTitle,
    rowAriaLabel,
    sx,
    children,
}) => {
    const { rootDropProps, dropHighlightSx } = usePlainTextClientDrop({
        enabled,
        onTextDropped,
        onFileOnlyDrop,
    });

    return (
        <TableRow
            {...rootDropProps}
            onDoubleClick={onDoubleClick}
            title={rowTitle}
            aria-label={rowAriaLabel}
            sx={{
                transition: 'background-color 0.15s ease, outline 0.15s ease',
                ...sx,
                ...dropHighlightSx,
            }}
        >
            {children}
        </TableRow>
    );
};

const ClientManagementPage: React.FC = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const navigate = useNavigate();
    const { user } = useAuth();

    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
    const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';
    const headerBg = isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04);
    const controlBg = isDark ? alpha(NAUTILUS.abyssal, 0.6) : alpha(NAUTILUS.deepOcean, 0.03);
    const ink = isDark ? '#ffffff' : NAUTILUS.deepOcean;
    const inkSecondary = isDark ? '#b0bec5' : '#546e7a';
    const inkTertiary = isDark ? '#90a4ae' : '#78909c';

    const technicalLabelSx = useMemo(
        () => ({
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase' as const,
            color: inkSecondary,
        }),
        [inkSecondary],
    );

    const textFieldSx = useMemo(
        () => ({
            '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                bgcolor: controlBg,
                '& fieldset': { borderColor: panelBorder },
                '&:hover fieldset': {
                    borderColor: isDark ? alpha(NAUTILUS.sand, 0.35) : alpha(NAUTILUS.deepOcean, 0.25),
                },
                '&.Mui-focused fieldset': { borderColor: NAUTILUS.sand },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: NAUTILUS.sand },
        }),
        [controlBg, panelBorder, isDark],
    );

    const tableHeaderCellSx = {
        fontWeight: 600,
        fontSize: '0.7rem',
        letterSpacing: '0.05em',
        textTransform: 'uppercase' as const,
        color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
        borderBottom: 'none',
        py: 1.5,
        bgcolor: headerBg,
    };

    const dialogPaperSx = {
        bgcolor: surfaceBg,
        border: `1px solid ${panelBorder}`,
        borderRadius: 1,
        boxShadow: isDark ? `0 16px 48px ${alpha('#000000', 0.45)}` : `0 16px 48px ${alpha(NAUTILUS.deepOcean, 0.12)}`,
    };

    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [formData, setFormData] = useState({ name: '', abbreviation: '' });
    const [formKind, setFormKind] = useState<ClientKind>('client');
    const [formBlacklisted, setFormBlacklisted] = useState(false);
    const [pocRows, setPocRows] = useState<PocFormRow[]>([emptyPocRow()]);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
    const [openConvertDialog, setOpenConvertDialog] = useState(false);
    const [clientToConvert, setClientToConvert] = useState<Client | null>(null);
    const [kindFilter, setKindFilter] = useState<KindFilter>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [extracting, setExtracting] = useState(false);
    const fieldErrors = useFormFieldErrors();

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        try {
            setLoading(true);
            const clientsData = await projectsService.getClients();
            setClients(clientsData);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to load clients');
        } finally {
            setLoading(false);
        }
    };

    const defaultKindForCreate = (): ClientKind => (kindFilter === 'lead' ? 'lead' : 'client');

    const handleOpenCreateDialog = () => {
        setDialogMode('create');
        setFormData({ name: '', abbreviation: '' });
        setFormKind(defaultKindForCreate());
        setFormBlacklisted(false);
        setPocRows([emptyPocRow()]);
        setSelectedClient(null);
        fieldErrors.clear();
        setOpenDialog(true);
    };

    const handleOpenEditDialog = (client: Client) => {
        setDialogMode('edit');
        setFormData({
            name: client.name,
            abbreviation: client.abbreviation || '',
        });
        setFormKind(contactKind(client));
        setFormBlacklisted(Boolean(client.is_blacklisted));
        setPocRows(clientToPocRows(client));
        setSelectedClient(client);
        fieldErrors.clear();
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        fieldErrors.clear();
        setFormData({ name: '', abbreviation: '' });
        setFormKind('client');
        setFormBlacklisted(false);
        setPocRows([emptyPocRow()]);
        setSelectedClient(null);
    };

    const buildPocsPayload = () =>
        pocRows
            .map((r) => ({
                full_name: r.full_name.trim(),
                email: r.email.trim() || undefined,
                phone: r.phone.trim() || undefined,
                job_position: r.job_position.trim() || undefined,
            }))
            .filter((r) => r.full_name.length > 0);

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['name']);
            return;
        }
        try {
            setLoading(true);
            fieldErrors.clear();

            const pocs = buildPocsPayload();

            if (dialogMode === 'create') {
                await projectsService.createClient({
                    name: formData.name.trim(),
                    kind: formKind,
                    is_blacklisted: formBlacklisted,
                    abbreviation: formData.abbreviation.trim() || undefined,
                    pocs,
                });
                setSuccess(`${formKind === 'lead' ? 'Lead' : 'Client'} created successfully`);
            } else if (selectedClient) {
                await projectsService.updateClient(selectedClient.id, {
                    name: formData.name.trim(),
                    kind: formKind,
                    is_blacklisted: formBlacklisted,
                    abbreviation: formData.abbreviation.trim(),
                    pocs,
                });
                setSuccess('Contact updated successfully');
            }

            handleCloseDialog();
            await loadClients();

            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            fieldErrors.show(err.response?.data?.error || `Failed to ${dialogMode} client`);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDeleteDialog = (client: Client) => {
        setClientToDelete(client);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setClientToDelete(null);
        setOpenDeleteDialog(false);
    };

    const handleDelete = async () => {
        if (!clientToDelete) return;
        if ((clientToDelete.project_count ?? 0) > 0) {
            setError(
                `Cannot delete contact. ${clientToDelete.project_count} project(s) are associated with this client.`,
            );
            handleCloseDeleteDialog();
            return;
        }

        try {
            setLoading(true);
            setError(null);
            await projectsService.deleteClient(clientToDelete.id);
            setSuccess('Contact deleted successfully');
            handleCloseDeleteDialog();
            await loadClients();

            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to delete contact');
            handleCloseDeleteDialog();
        } finally {
            setLoading(false);
        }
    };

    const handleOpenConvertDialog = (client: Client) => {
        setClientToConvert(client);
        setOpenConvertDialog(true);
    };

    const handleCloseConvertDialog = () => {
        setClientToConvert(null);
        setOpenConvertDialog(false);
    };

    const handleConvertToClient = async () => {
        if (!clientToConvert) return;

        try {
            setLoading(true);
            setError(null);
            await projectsService.updateClient(clientToConvert.id, { kind: 'client' });
            setSuccess(`"${clientToConvert.name}" is now a client`);
            handleCloseConvertDialog();
            await loadClients();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to convert lead to client');
            handleCloseConvertDialog();
        } finally {
            setLoading(false);
        }
    };

    const updatePocRow = (index: number, field: keyof PocFormRow, value: string) => {
        setPocRows((rows) => {
            const next = [...rows];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const addPocRow = () => setPocRows((rows) => [...rows, emptyPocRow()]);

    const removePocRow = (index: number) => {
        setPocRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, i) => i !== index)));
    };

    const applyExtractedPayload = useCallback(
        (data: {
            name: string;
            abbreviation: string;
            pocs: Array<{ full_name: string; email?: string; phone: string; job_position: string }>;
        }) => {
            setFormData({
                name: data.name || '',
                abbreviation: (data.abbreviation || '').slice(0, 2).toUpperCase(),
            });
            const rows =
                data.pocs?.length > 0
                    ? data.pocs.map((p) => ({
                          full_name: p.full_name || '',
                          email: p.email || '',
                          phone: p.phone || '',
                          job_position: p.job_position || '',
                      }))
                    : [emptyPocRow()];
            setPocRows(rows);
        },
        [],
    );

    const handleCreateDialogDrop = useCallback(
        async (text: string) => {
            setError(null);
            setExtracting(true);
            try {
                const data = await extractClientFromText(text);
                applyExtractedPayload(data);
                setSuccess('Form prefilled from dropped text — review before saving.');
                setTimeout(() => setSuccess(null), 5000);
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'Failed to extract client data';
                setError(msg);
            } finally {
                setExtracting(false);
            }
        },
        [applyExtractedPayload],
    );

    const handleClientRowDrop = useCallback(async (client: Client, text: string) => {
        setError(null);
        setExtracting(true);
        try {
            const data = await extractClientFromText(text);
            const extractedRows = mapExtractedPocs(data.pocs);
            if (extractedRows.length === 0) {
                setError('No contact details found in dropped text.');
                return;
            }

            setDialogMode('edit');
            setSelectedClient(client);
            setFormKind(contactKind(client));
            setFormBlacklisted(Boolean(client.is_blacklisted));
            setFormData({
                name: client.name,
                abbreviation: client.abbreviation || '',
            });
            setPocRows(mergePocRows(clientToPocRows(client), extractedRows));
            setOpenDialog(true);
            setSuccess(`Contact details extracted for "${client.name}" — review before saving.`);
            setTimeout(() => setSuccess(null), 5000);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Failed to extract contact data';
            setError(msg);
        } finally {
            setExtracting(false);
        }
    }, []);

    const canManageClients = hasCapability(user, CAP.MANAGE_CLIENTS);

    const kindFilteredClients = useMemo(() => {
        if (kindFilter === 'all') return clients;
        return clients.filter((client) => contactKind(client) === kindFilter);
    }, [clients, kindFilter]);

    const filteredClients = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return kindFilteredClients;
        return kindFilteredClients.filter((client) => clientSearchHaystack(client).includes(query));
    }, [kindFilteredClients, searchQuery]);

    const onFileOnlyDrop = useCallback(
        () => setError('Only plain text can be dropped here, not files.'),
        [],
    );

    const { dialogPaperDropProps } = usePlainTextClientDrop({
        enabled: canManageClients && openDialog && dialogMode === 'create',
        onTextDropped: handleCreateDialogDrop,
        onFileOnlyDrop,
    });

    const renderCallSign = (abbr: string | undefined) => {
        if (!abbr) {
            return (
                <Typography component="span" sx={{ color: inkTertiary, fontFamily: 'monospace' }}>
                    —
                </Typography>
            );
        }
        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-block',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    border: `1px solid ${panelBorder}`,
                    bgcolor: isDark ? alpha(NAUTILUS.sand, 0.1) : alpha(NAUTILUS.deepOcean, 0.04),
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    letterSpacing: '0.12em',
                    color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                }}
            >
                {abbr}
            </Box>
        );
    };

    const handleClientRowDoubleClick = useCallback(
        (client: Client) => {
            if (contactKind(client) !== 'client') return;
            navigate(`/projects?client=${encodeURIComponent(client.name)}`);
        },
        [navigate],
    );

    const renderProjectsCell = (client: Client) => {
        if (contactKind(client) === 'lead') {
            return (
                <Typography variant="body2" sx={{ color: inkTertiary }}>
                    —
                </Typography>
            );
        }

        const byType = client.projects_by_type?.filter(
            (entry) => entry.count > 0 && entry.project_type !== PROJECT_TYPE_PROPOSAL,
        ) ?? [];
        if (byType.length === 0) {
            return (
                <Typography variant="body2" sx={{ color: inkTertiary }}>
                    —
                </Typography>
            );
        }

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {byType.map((entry) => (
                    <Chip
                        key={entry.project_type}
                        label={`${entry.description} ${entry.count}`}
                        size="small"
                        sx={{
                            height: 22,
                            borderRadius: 1,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            bgcolor: isDark
                                ? alpha(NAUTILUS.sand, 0.18)
                                : alpha(NAUTILUS.deepOcean, 0.08),
                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                            border: `1px solid ${
                                isDark
                                    ? alpha(NAUTILUS.sand, 0.35)
                                    : alpha(NAUTILUS.deepOcean, 0.2)
                            }`,
                        }}
                    />
                ))}
            </Box>
        );
    };

    const renderPocCell = (client: Client) => {
        const pocs = (client.pocs || []).filter((p) => p.full_name);
        if (pocs.length === 0) {
            return (
                <Typography variant="body2" sx={{ color: inkTertiary }}>
                    No contacts
                </Typography>
            );
        }
        const lead = pocs[0];
        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                    <Chip
                        label={`${pocs.length} contact${pocs.length === 1 ? '' : 's'}`}
                        size="small"
                        sx={{
                            height: 22,
                            borderRadius: 1,
                            border: `1px solid ${panelBorder}`,
                            bgcolor: 'transparent',
                            color: inkSecondary,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                        }}
                    />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: ink }}>
                    {lead.full_name}
                </Typography>
                {(lead.email || lead.job_position) && (
                    <Typography variant="caption" sx={{ color: inkSecondary, display: 'block' }}>
                        {[lead.email, lead.job_position].filter(Boolean).join(' · ')}
                    </Typography>
                )}
                {pocs.length > 1 && (
                    <Typography variant="caption" sx={{ color: inkTertiary }}>
                        +{pocs.length - 1} more
                    </Typography>
                )}
            </Box>
        );
    };

    if (!canManageClients) {
        return (
            <AccessDenied
                title="Access denied"
                message="You do not have permission to manage contacts."
            />
        );
    }

    if (loading && clients.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <LoadingAnimation />
            </Box>
        );
    }

    const abbrInvalid = Boolean(formData.abbreviation) && formData.abbreviation.length !== 2;
    const filledPocCount = pocRows.filter((r) => r.full_name.trim()).length;

    return (
        <Box sx={{ position: 'relative', minHeight: 240 }}>
            {extracting && (
                <LinearProgress
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: (t) => t.zIndex.modal + 2,
                        '& .MuiLinearProgress-bar': { bgcolor: NAUTILUS.sand },
                    }}
                />
            )}

            {/* Page header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={2}
                mb={3}
                sx={{ pb: 2, borderBottom: `1px solid ${panelBorder}` }}
            >
                <Box>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: ink }}
                    >
                        Contact List
                    </Typography>
                    <Typography variant="body2" sx={{ color: inkSecondary, mt: 0.5 }}>
                        Contact registry for clients and leads — points of contact for projects and transmittals
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateDialog}
                    aria-label="Add new contact"
                    sx={{
                        bgcolor: NAUTILUS.sand,
                        color: NAUTILUS.deepOcean,
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        px: 3,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' },
                    }}
                >
                    New Contact
                </Button>
            </Box>

            {/* Text intake panel */}
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 1,
                    border: `1px solid ${panelBorder}`,
                    bgcolor: isDark ? alpha(NAUTILUS.sand, 0.06) : alpha(NAUTILUS.deepOcean, 0.03),
                    borderLeft: `3px solid ${NAUTILUS.sand}`,
                }}
            >
                <Typography sx={{ ...technicalLabelSx, mb: 0.75 }}>Text intake</Typography>
                <Typography variant="body2" sx={{ color: inkSecondary, maxWidth: 720, lineHeight: 1.6 }}>
                    Drop <strong style={{ color: ink }}>plain text</strong> onto a contact row to extract contact
                    details for that company. To create a new contact from email text, open{' '}
                    <strong style={{ color: ink }}>New Contact</strong> and drop text inside the dialog.
                </Typography>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 3, borderRadius: 1, border: `1px solid ${theme.palette.error.main}` }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {success && (
                <Alert
                    severity="success"
                    sx={{ mb: 3, borderRadius: 1, border: `1px solid ${theme.palette.success.main}` }}
                    onClose={() => setSuccess(null)}
                >
                    {success}
                </Alert>
            )}

            {/* Registry panel */}
            <Card
                sx={{
                    bgcolor: surfaceBg,
                    border: `1px solid ${panelBorder}`,
                    borderRadius: 1,
                    boxShadow: 'none',
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${panelBorder}`,
                        bgcolor: headerBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ fontSize: '1.1rem', color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />
                        <Typography sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: ink, fontSize: '0.95rem' }}>
                            Contact registry
                        </Typography>
                    </Box>
                    <Chip
                        label={
                            searchQuery.trim() || kindFilter !== 'all'
                                ? `${filteredClients.length} of ${kindFilteredClients.length}`
                                : `${clients.length} record${clients.length === 1 ? '' : 's'}`
                        }
                        size="small"
                        sx={{
                            height: 24,
                            borderRadius: 1,
                            border: `1px solid ${panelBorder}`,
                            bgcolor: 'transparent',
                            color: inkSecondary,
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            letterSpacing: '0.04em',
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${panelBorder}`,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 1.5,
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by name, call sign, or contact…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: '1.1rem', color: inkTertiary }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={textFieldSx}
                    />
                    <ToggleButtonGroup
                        value={kindFilter}
                        exclusive
                        onChange={(_, value: KindFilter | null) => {
                            if (value) setKindFilter(value);
                        }}
                        size="small"
                        aria-label="Filter by contact kind"
                        sx={{
                            flexShrink: 0,
                            '& .MuiToggleButton-root': {
                                px: 1.5,
                                py: 0.5,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderColor: panelBorder,
                                color: inkSecondary,
                                '&.Mui-selected': {
                                    bgcolor: isDark
                                        ? alpha(NAUTILUS.sand, 0.15)
                                        : alpha(NAUTILUS.deepOcean, 0.08),
                                    color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                    borderColor: panelBorder,
                                    '&:hover': {
                                        bgcolor: isDark
                                            ? alpha(NAUTILUS.sand, 0.22)
                                            : alpha(NAUTILUS.deepOcean, 0.12),
                                    },
                                },
                            },
                        }}
                    >
                        <ToggleButton value="client" aria-label="Clients only">
                            Clients
                        </ToggleButton>
                        <ToggleButton value="lead" aria-label="Leads only">
                            Leads
                        </ToggleButton>
                        <ToggleButton value="all" aria-label="All contacts">
                            All
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <CardContent sx={{ p: 0 }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ borderBottom: `1px solid ${panelBorder}` }}>
                                    <TableCell sx={tableHeaderCellSx}>Company</TableCell>
                                    <TableCell sx={tableHeaderCellSx}>Call sign</TableCell>
                                    <TableCell sx={tableHeaderCellSx}>Points of contact</TableCell>
                                    <TableCell sx={tableHeaderCellSx}>Projects</TableCell>
                                    <TableCell align="right" sx={tableHeaderCellSx}>
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clients.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ borderBottom: 'none', py: 6 }}>
                                            <Typography sx={{ fontWeight: 600, color: ink }}>
                                                No contacts in registry
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: inkSecondary, mt: 0.5 }}>
                                                Use New Contact to add your first record.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredClients.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ borderBottom: 'none', py: 6 }}>
                                            <Typography sx={{ fontWeight: 600, color: ink }}>
                                                No contacts match your search
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: inkSecondary, mt: 0.5 }}>
                                                Try a different name, call sign, or contact detail.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredClients.map((client, index) => {
                                        const isClientRow = contactKind(client) === 'client';
                                        return (
                                        <ClientRowDropTarget
                                            key={client.id}
                                            enabled={canManageClients}
                                            onTextDropped={(text) => handleClientRowDrop(client, text)}
                                            onFileOnlyDrop={onFileOnlyDrop}
                                            onDoubleClick={
                                                isClientRow
                                                    ? () => handleClientRowDoubleClick(client)
                                                    : undefined
                                            }
                                            rowTitle={
                                                isClientRow
                                                    ? `View projects for ${client.name} (double-click)`
                                                    : undefined
                                            }
                                            rowAriaLabel={
                                                isClientRow
                                                    ? `View projects for ${client.name}`
                                                    : undefined
                                            }
                                            sx={{
                                                cursor: isClientRow ? 'pointer' : undefined,
                                                '&:hover': {
                                                    bgcolor: isDark
                                                        ? alpha(NAUTILUS.sand, 0.05)
                                                        : alpha(NAUTILUS.deepOcean, 0.02),
                                                },
                                                borderBottom:
                                                    index === filteredClients.length - 1
                                                        ? 'none'
                                                        : `1px solid ${alpha(panelBorder, 0.5)}`,
                                            }}
                                        >
                                            <TableCell sx={{ borderBottom: 'none', py: 1.75 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Typography sx={{ fontWeight: 600, color: ink }}>
                                                        {client.name}
                                                    </Typography>
                                                    <ContactKindChip kind={contactKind(client)} />
                                                    {client.is_blacklisted ? <ContactBlacklistChip /> : null}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none', py: 1.75 }}>
                                                {renderCallSign(client.abbreviation)}
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none', py: 1.75 }}>
                                                {renderPocCell(client)}
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: inkTertiary, display: 'block', mt: 0.75 }}
                                                >
                                                    Drop email text here to add a contact
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none', py: 1.75 }}>
                                                {renderProjectsCell(client)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ borderBottom: 'none', py: 1.75 }}
                                                onDoubleClick={(e) => e.stopPropagation()}
                                            >
                                                {contactKind(client) === 'lead' && (
                                                    <Tooltip title="Convert to client">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenConvertDialog(client)}
                                                            aria-label={`Convert ${client.name} to client`}
                                                            sx={{
                                                                color: isDark ? '#90caf9' : NAUTILUS.deepOcean,
                                                                '&:hover': {
                                                                    bgcolor: isDark
                                                                        ? alpha('#90caf9', 0.12)
                                                                        : alpha(NAUTILUS.deepOcean, 0.06),
                                                                },
                                                            }}
                                                        >
                                                            <ConvertIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Edit contact">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEditDialog(client)}
                                                        aria-label={`Edit ${client.name}`}
                                                        sx={{
                                                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                                            '&:hover': {
                                                                bgcolor: isDark
                                                                    ? alpha(NAUTILUS.sand, 0.12)
                                                                    : alpha(NAUTILUS.deepOcean, 0.06),
                                                            },
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip
                                                    title={
                                                        (client.project_count ?? 0) > 0
                                                            ? `Cannot delete — assigned to ${client.project_count} project(s)`
                                                            : 'Delete contact'
                                                    }
                                                >
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenDeleteDialog(client)}
                                                            aria-label={`Delete ${client.name}`}
                                                            disabled={(client.project_count ?? 0) > 0}
                                                            sx={{
                                                                color: theme.palette.error.main,
                                                                '&:hover': {
                                                                    bgcolor: alpha(theme.palette.error.main, 0.1),
                                                                },
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </ClientRowDropTarget>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Create / Edit dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    ...dialogPaperDropProps,
                    sx: { ...dialogPaperSx, ...dialogPaperDropProps?.sx },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: `1px solid ${panelBorder}`,
                        py: 2,
                        px: 3,
                    }}
                >
                    <Typography sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: ink, fontSize: '1.1rem' }}>
                        {dialogMode === 'create' ? 'Create contact' : 'Edit contact'}
                    </Typography>
                    {dialogMode === 'edit' && selectedClient && (
                        <Typography variant="body2" sx={{ color: inkSecondary, mt: 0.5 }}>
                            Record #{selectedClient.id} · {selectedClient.name}
                        </Typography>
                    )}
                    {dialogMode === 'create' && (
                        <Typography variant="body2" sx={{ color: inkSecondary, mt: 0.5 }}>
                            Drop plain text anywhere in this dialog to extract company name, call sign, and contacts.
                        </Typography>
                    )}
                </DialogTitle>
                <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
                <DialogContent sx={{ px: 3, py: 3 }}>
                    <Typography sx={{ ...technicalLabelSx, mb: 1.5 }}>Contact type</Typography>
                    <ToggleButtonGroup
                        value={formKind}
                        exclusive
                        onChange={(_, value: ClientKind | null) => {
                            if (value) setFormKind(value);
                        }}
                        size="small"
                        aria-label="Contact kind"
                        sx={{
                            mb: 3,
                            '& .MuiToggleButton-root': {
                                px: 2,
                                py: 0.5,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderColor: panelBorder,
                                color: inkSecondary,
                                '&.Mui-selected': {
                                    bgcolor: isDark
                                        ? alpha(NAUTILUS.sand, 0.15)
                                        : alpha(NAUTILUS.deepOcean, 0.08),
                                    color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                    borderColor: panelBorder,
                                },
                            },
                        }}
                    >
                        <ToggleButton value="client" aria-label="Client">
                            Client
                        </ToggleButton>
                        <ToggleButton
                            value="lead"
                            aria-label="Lead"
                            disabled={
                                dialogMode === 'edit' &&
                                (selectedClient?.project_count ?? 0) > 0 &&
                                contactKind(selectedClient!) === 'client'
                            }
                        >
                            Lead
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Typography sx={{ ...technicalLabelSx, mb: 2 }}>Company record</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1.5 }}>
                        <TextField
                            fullWidth
                            label="Company name"
                            value={formData.name}
                            error={fieldErrors.invalid('name')}
                            onChange={(e) => {
                                fieldErrors.clearField('name');
                                setFormData({ ...formData, name: e.target.value });
                            }}
                            required
                            autoFocus
                            sx={textFieldSx}
                        />
                        <TextField
                            label="Call sign"
                            value={formData.abbreviation}
                            onChange={(e) =>
                                setFormData({ ...formData, abbreviation: e.target.value.toUpperCase() })
                            }
                            placeholder="ND"
                            helperText={abbrInvalid ? 'Must be exactly 2 letters' : '2 capital letters, or empty'}
                            error={abbrInvalid}
                            inputProps={{ maxLength: 2, style: { fontFamily: 'monospace', letterSpacing: '0.15em' } }}
                            sx={{ ...textFieldSx, minWidth: { sm: 160 }, maxWidth: { sm: 200 } }}
                        />
                    </Stack>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={formBlacklisted}
                                onChange={(_, checked) => setFormBlacklisted(checked)}
                            />
                        }
                        label="Blacklisted"
                        sx={{ mb: 4, ml: 0, userSelect: 'none', color: inkSecondary }}
                    />

                    {/* Points of contact section */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            pb: 1.5,
                            mb: 2,
                            borderBottom: `1px solid ${panelBorder}`,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ContactsIcon sx={{ fontSize: '1rem', color: inkSecondary }} />
                            <Typography sx={{ ...technicalLabelSx, mb: 0 }}>Points of contact</Typography>
                            <Chip
                                label={`${filledPocCount} slot${filledPocCount === 1 ? '' : 's'}`}
                                size="small"
                                sx={{
                                    height: 20,
                                    ml: 0.5,
                                    borderRadius: 1,
                                    border: `1px solid ${panelBorder}`,
                                    bgcolor: 'transparent',
                                    color: inkTertiary,
                                    fontSize: '0.6rem',
                                    fontWeight: 600,
                                }}
                            />
                        </Box>
                        <Button
                            size="small"
                            startIcon={<PersonAddIcon />}
                            onClick={addPocRow}
                            aria-label="Add point of contact"
                            sx={{
                                borderRadius: 1,
                                border: `1px solid ${panelBorder}`,
                                color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                px: 1.5,
                                '&:hover': {
                                    bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04),
                                    borderColor: isDark ? alpha(NAUTILUS.sand, 0.35) : panelBorder,
                                },
                            }}
                        >
                            Add slot
                        </Button>
                    </Box>

                    <Typography variant="body2" sx={{ color: inkSecondary, mb: 2 }}>
                        Full name required per slot. Email, phone, and role are optional; empty slots are ignored on
                        save.
                    </Typography>

                    <Stack spacing={2}>
                        {pocRows.map((row, index) => (
                            <Box
                                key={index}
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    border: `1px solid ${panelBorder}`,
                                    bgcolor: isDark ? alpha(NAUTILUS.abyssal, 0.35) : alpha(NAUTILUS.deepOcean, 0.02),
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1.5,
                                    }}
                                >
                                    <Typography sx={{ ...technicalLabelSx, color: isDark ? NAUTILUS.sand : inkSecondary }}>
                                        Contact {String(index + 1).padStart(2, '0')}
                                    </Typography>
                                    <Tooltip title="Remove contact slot">
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label="Remove contact"
                                                onClick={() => removePocRow(index)}
                                                disabled={pocRows.length <= 1}
                                                sx={{
                                                    color: theme.palette.error.main,
                                                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) },
                                                }}
                                            >
                                                <RemovePocIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Full name"
                                    value={row.full_name}
                                    onChange={(e) => updatePocRow(index, 'full_name', e.target.value)}
                                    sx={{ ...textFieldSx, mb: 1.5 }}
                                />
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                                        gap: 1.5,
                                    }}
                                >
                                    <TextField
                                        size="small"
                                        label="Email"
                                        type="email"
                                        value={row.email}
                                        onChange={(e) => updatePocRow(index, 'email', e.target.value)}
                                        sx={textFieldSx}
                                    />
                                    <TextField
                                        size="small"
                                        label="Phone"
                                        value={row.phone}
                                        onChange={(e) => updatePocRow(index, 'phone', e.target.value)}
                                        sx={textFieldSx}
                                    />
                                    <TextField
                                        size="small"
                                        label="Job position"
                                        value={row.job_position}
                                        onChange={(e) => updatePocRow(index, 'job_position', e.target.value)}
                                        sx={textFieldSx}
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: `1px solid ${panelBorder}`,
                        gap: 1,
                    }}
                >
                    <Button
                        onClick={handleCloseDialog}
                        sx={{ borderRadius: 1, px: 2.5, color: inkSecondary }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={abbrInvalid || loading || extracting}
                        sx={{
                            borderRadius: 1,
                            px: 3,
                            fontWeight: 600,
                            boxShadow: 'none',
                            bgcolor: NAUTILUS.sand,
                            color: NAUTILUS.deepOcean,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontSize: '0.75rem',
                            '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' },
                            '&.Mui-disabled': {
                                bgcolor: alpha(NAUTILUS.sand, 0.35),
                                color: alpha(NAUTILUS.deepOcean, 0.5),
                            },
                        }}
                    >
                        {dialogMode === 'create' ? 'Create contact' : 'Save changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Convert to client confirmation */}
            <Dialog
                open={openConvertDialog}
                onClose={handleCloseConvertDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: `1px solid ${panelBorder}`,
                        py: 2,
                        px: 3,
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        color: ink,
                    }}
                >
                    Convert to client
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2.5 }}>
                    <Typography sx={{ color: ink }}>
                        Convert lead <strong>{clientToConvert?.name}</strong> to a client?
                    </Typography>
                    <Typography variant="body2" sx={{ color: inkSecondary, mt: 1.5, lineHeight: 1.6 }}>
                        Clients can be assigned to any project. Leads can be assigned to proposal projects only; convert to client for other project types.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}`, gap: 1 }}>
                    <Button onClick={handleCloseConvertDialog} sx={{ color: inkSecondary }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConvertToClient}
                        disabled={loading}
                        sx={{
                            borderRadius: 1,
                            fontWeight: 600,
                            boxShadow: 'none',
                            bgcolor: NAUTILUS.sand,
                            color: NAUTILUS.deepOcean,
                            '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' },
                        }}
                    >
                        Convert
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: `1px solid ${panelBorder}`,
                        py: 2,
                        px: 3,
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        color: ink,
                    }}
                >
                    Confirm delete
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2.5 }}>
                    <Typography sx={{ color: ink }}>
                        Delete contact <strong>{clientToDelete?.name}</strong>?
                    </Typography>
                    <Typography variant="body2" sx={{ color: inkSecondary, mt: 1.5, lineHeight: 1.6 }}>
                        This cannot be undone. Deletion is only allowed when no projects reference this client.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}`, gap: 1 }}>
                    <Button onClick={handleCloseDeleteDialog} sx={{ color: inkSecondary }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={loading}
                        sx={{ borderRadius: 1, fontWeight: 600, boxShadow: 'none' }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClientManagementPage;
